// Közös űrlap-mező-készlet — react-hook-form + a shadcn Form-primitívek (FormField/FormItem/FormLabel/
// FormControl/FormMessage) fölé húzott, magas szintű mezők: text/email/tel input, switch, select (dropdown),
// és pill-gombos választó (status). Label + hiba + hint egységesen. A mezőket egy `<Form {...form}>`
// (FormProvider) alá kell rendelni — a Controller a kontextusból veszi a `control`-t.
//
// A sheet-alapú modulok (contacts) FormSheet-je ezekre a mezőkre épít majd — ezért közös (shared) réteg.
import { type ReactNode, useRef } from 'react';
import { type FieldPath, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EntityCombobox, type ComboboxOption } from '@/components/shared/entity-combobox';
import { DatePicker } from '@/components/ui/date-picker';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Segmented, type SegmentedOption } from '@/components/ui/segmented';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface BaseFieldProps<TValues extends FieldValues> {
  name: FieldPath<TValues>;
  label: string;
  hint?: string;
  disabled?: boolean;
  required?: boolean;
}

/** Async entitás-választó mező (EntityCombobox RHF-be kötve) — nagy listákhoz (fiók/kapcsolattartó). A form
 *  a kiválasztott id-t tárolja (`''` = nincs). */
export function ComboboxField<TValues extends FieldValues>({
  name,
  label,
  hint,
  required,
  search,
  initialOption,
  placeholder,
  clearable,
}: BaseFieldProps<TValues> & {
  search: (q: string) => Promise<ComboboxOption[]>;
  initialOption?: ComboboxOption | null;
  placeholder?: string;
  /** Törölhető-e a kiválasztás (X). Alap: true; kötelező, mindig-kitöltött mezőnél (pl. ország) add `false`-t. */
  clearable?: boolean;
}): JSX.Element {
  return (
    <FormField<TValues>
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label}
            {required && <span className="text-destructive"> *</span>}
          </FormLabel>
          <EntityCombobox
            value={typeof field.value === 'string' && field.value !== '' ? field.value : null}
            onChange={(id) => field.onChange(id ?? '')}
            search={search}
            initialOption={initialOption ?? null}
            placeholder={placeholder}
            clearable={clearable}
            aria-label={label}
          />
          {hint && <p className="text-subtle text-xs">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** Szöveges mező (text / email / tel). Üres string a default; a contract preprocess-e alakítja null-ra. */
export function TextField<TValues extends FieldValues>({
  name,
  label,
  hint,
  disabled,
  required,
  type = 'text',
  placeholder,
  autoComplete,
  inputMode,
  leadingIcon,
  hideLabel = false,
}: BaseFieldProps<TValues> & {
  type?: 'text' | 'email' | 'tel' | 'password';
  placeholder?: string;
  autoComplete?: string;
  /** Virtuális billentyűzet / bevitel-mód (pl. `numeric` a szám-mezőknél) — a típus marad `text` (szabad kontroll). */
  inputMode?: 'text' | 'numeric' | 'decimal' | 'tel' | 'email' | 'url' | 'search';
  /**
   * VEZETŐ IKON az input BELSEJÉBEN, balra (a `DatePicker` naptár-ikonjának mintája). Tisztán dekoratív:
   * `pointer-events-none`, és a szöveg elé `pl-9` behúzást kap — a kattintás így is a mezőt fókuszálja.
   */
  leadingIcon?: ReactNode;
  /**
   * A CÍMKE elrejtése (a `label` ilyenkor a mező `aria-label`-je lesz). CSOPORTOS mezőkhöz: ha több input
   * EGY közös címke alatt áll (pl. a három link-mező a „Linkek" alatt), a per-mező látható címke zaj lenne —
   * de a képernyőolvasónak SZÜKSÉGE van a mező nevére, ezért NEM hagyjuk el, csak elrejtjük.
   */
  hideLabel?: boolean;
}): JSX.Element {
  return (
    <FormField<TValues>
      name={name}
      render={({ field }) => (
        <FormItem>
          {!hideLabel && (
            <FormLabel>
              {label}
              {required && <span className="text-destructive"> *</span>}
            </FormLabel>
          )}
          <FormControl>
            {/* A VEZETŐ IKON burkolatát az `Input` ATOM rendereli (`leadingIcon`) — korábban itt állt kézzel,
                de a partner jegy-nyitója RHF nélküli `Input`-okon kérte ugyanezt a megjelenést, és két
                példány garantáltan elcsúszott volna. */}
            <Input
              type={type}
              inputMode={inputMode}
              placeholder={placeholder}
              autoComplete={autoComplete}
              disabled={disabled}
              {...(leadingIcon !== undefined ? { leadingIcon } : {})}
              {...(hideLabel ? { 'aria-label': label } : {})}
              {...field}
              value={typeof field.value === 'string' ? field.value : ''}
            />
          </FormControl>
          {hint && <p className="text-subtle text-xs">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** Jelszó-mező RHF-be kötve, MINDIG szemecske-toggle-lel (megtekintés/elrejtés) — a `TextField` mása,
 *  de a `PasswordInput`-ra épül. Jelszó-jellegű titkokhoz (jelenlegi/új jelszó), NEM az Asana PAT-hoz. */
export function PasswordField<TValues extends FieldValues>({
  name,
  label,
  hint,
  disabled,
  required,
  placeholder,
  autoComplete,
}: BaseFieldProps<TValues> & {
  placeholder?: string;
  autoComplete?: string;
}): JSX.Element {
  return (
    <FormField<TValues>
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label}
            {required && <span className="text-destructive"> *</span>}
          </FormLabel>
          <FormControl>
            <PasswordInput
              placeholder={placeholder}
              autoComplete={autoComplete}
              disabled={disabled}
              {...field}
              value={typeof field.value === 'string' ? field.value : ''}
            />
          </FormControl>
          {hint && <p className="text-subtle text-xs">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** Több-soros szöveges mező (textarea). Üres string a default; a contract preprocess-e alakítja null-ra. */
export function TextareaField<TValues extends FieldValues>({
  name,
  label,
  hint,
  disabled,
  required,
  placeholder,
  rows = 3,
}: BaseFieldProps<TValues> & {
  placeholder?: string;
  rows?: number;
}): JSX.Element {
  return (
    <FormField<TValues>
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label}
            {required && <span className="text-destructive"> *</span>}
          </FormLabel>
          <FormControl>
            <Textarea
              rows={rows}
              placeholder={placeholder}
              disabled={disabled}
              {...field}
              value={typeof field.value === 'string' ? field.value : ''}
            />
          </FormControl>
          {hint && <p className="text-subtle text-xs">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** Rich-text mező (RichTextEditor RHF-be kötve) — a mező HTML-string-et tárol. WYSIWYG (félkövér/dőlt/lista/link).
 *  A megjelenítés MINDIG a `RichTextContent` (DOMPurify). A `value` HTML; üres string a default. */
export function RichTextField<TValues extends FieldValues>({
  name,
  label,
  hint,
  disabled,
  required,
  placeholder,
  imageAccountId,
  status,
}: BaseFieldProps<TValues> & {
  placeholder?: string;
  /**
   * A beszúrható kép PREFERÁLT cél-fiókja (lásd `RichTextEditor.imageAccountId`): megadva az adott ügyfél
   * fájl-terébe kerül a kép, elhagyva a BELSŐ (MORGENS) ügyfélbe — a kép-gomb MINDIG működik. Régi doc: id → aktív ·
   * `null` → tiltott, magyarázattal · a prop ELHAGYVA → a kép-gomb meg sem jelenik.
   */
  imageAccountId?: string | null;
  /**
   * A mező alatti EGYETLEN visszajelzés-sor (pl. élő karakter-számláló), amely a hibaüzenet HELYÉRE lép: a
   * hiba-állapotot (`invalid`) megkapja, és maga mondja ki — így nem áll két üzenet egymás alatt (user-lelet: üres
   * jegy-leírásnál „A leírás legalább 25 karakter legyen." ÉS „Még 25 karakter kell"). Elhagyva a szokásos
   * `FormMessage` jelenik meg.
   */
  status?: (invalid: boolean) => ReactNode;
}): JSX.Element {
  return (
    <FormField<TValues>
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label}
            {required && <span className="text-destructive"> *</span>}
          </FormLabel>
          <FormControl>
            <RichTextEditor
              // A `undefined` ÁTMEGY (a prop elhagyása = a felület nem tud képet fogadni) — ezért nincs
              // default-érték; az `exactOptionalPropertyTypes` miatt a spread-forma kell.
              {...(imageAccountId === undefined ? {} : { imageAccountId })}
              value={typeof field.value === 'string' ? field.value : ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder={placeholder}
              disabled={disabled}
              aria-label={label}
            />
          </FormControl>
          {hint && <p className="text-subtle text-xs">{hint}</p>}
          {status ? <FieldStatus render={status} /> : <FormMessage />}
        </FormItem>
      )}
    />
  );
}

/** A `status` sor a `FormMessage` azonosítóján (ugyanott, ahol a hiba állna) és élő régióként (a változást felolvassa). */
function FieldStatus({ render }: { render: (invalid: boolean) => ReactNode }): JSX.Element {
  const { error, formMessageId } = useFormField();
  return (
    <div id={formMessageId} aria-live="polite">
      {render(error !== undefined)}
    </div>
  );
}

/** Dátum-mező (YYYY-MM-DD). A globális `DatePicker`-t (Popover + Calendar) használja — egységes a lista-szűrők
 *  dátumválasztójával. Üres string a default; a contract nullable date-re alakítja. */
export function DateField<TValues extends FieldValues>({
  name,
  label,
  hint,
  disabled,
  required,
}: BaseFieldProps<TValues>): JSX.Element {
  const { t, i18n } = useTranslation();
  return (
    <FormField<TValues>
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label}
            {required && <span className="text-destructive"> *</span>}
          </FormLabel>
          <FormControl>
            <DatePicker
              value={typeof field.value === 'string' ? field.value : ''}
              onChange={field.onChange}
              label={label}
              // A mező FELETT már ott a címke → a placeholder a VÁRT FORMÁTUM, nem a címke megismétlése.
              placeholder={t('common.datePlaceholder')}
              language={i18n.language}
              disabled={disabled ?? false}
            />
          </FormControl>
          {hint && <p className="text-subtle text-xs">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** Dátum+idő mező ('YYYY-MM-DDTHH:mm' HELYI idő). A globális `DateTimePicker`-t (Calendar + idő-mező) használja.
 *  A tz-konverziót (UTC↔helyi) a hívó végzi a submit/betöltés határán — a mező maga a helyi stringet tartja. */
export function DateTimeField<TValues extends FieldValues>({
  name,
  label,
  hint,
  disabled,
  required,
  minTime,
  maxTime,
}: BaseFieldProps<TValues> & {
  /** Az idő-rács ABLAKA (`HH:mm`) — a domén nyitva-tartása (pl. megbeszélés: 06:00–22:00). Alap: teljes nap. */
  minTime?: string;
  maxTime?: string;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  return (
    <FormField<TValues>
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label}
            {required && <span className="text-destructive"> *</span>}
          </FormLabel>
          <FormControl>
            <DateTimePicker
              value={typeof field.value === 'string' ? field.value : ''}
              onChange={field.onChange}
              label={label}
              placeholder={t('common.datePlaceholder')}
              timePlaceholder={t('common.timePlaceholder')}
              language={i18n.language}
              disabled={disabled ?? false}
              minTime={minTime}
              maxTime={maxTime}
            />
          </FormControl>
          {hint && <p className="text-subtle text-xs">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** Kapcsoló (bool). A label + a switch egy sorban; a hint a label alatt. */
export function SwitchField<TValues extends FieldValues>({
  name,
  label,
  hint,
  disabled,
}: BaseFieldProps<TValues>): JSX.Element {
  return (
    <FormField<TValues>
      name={name}
      render={({ field }) => (
        // `input-glass` → a keret/felület egyezik az input/select mezőkkel (halvány szegély mindkét témában).
        // Focus-ring itt NINCS: a konténer `div` nem fókuszálható (a `.input-glass:focus` csak a fókuszált
        // elemre hat), a kapcsoló a saját fókusz-stílusát viszi.
        <FormItem className="input-glass flex flex-row items-center justify-between gap-4 rounded-lg border p-3">
          <div className="space-y-0.5">
            <FormLabel>{label}</FormLabel>
            {hint && <p className="text-subtle text-xs">{hint}</p>}
          </div>
          <FormControl>
            <Switch
              checked={Boolean(field.value)}
              onCheckedChange={field.onChange}
              disabled={disabled}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

/** Legördülő választó (dropdown). Az opciók value→label párok. */
export function SelectField<TValues extends FieldValues>({
  name,
  label,
  hint,
  disabled,
  required,
  placeholder,
  options,
  groups,
}: BaseFieldProps<TValues> & {
  placeholder?: string;
  /** `disabled` opció → nem választható (pl. tulajdonos-státusz szerint tiltott hozzárendelés-státusz). */
  options?: { value: string; label: string; disabled?: boolean; depth?: number }[];
  /**
   * CSOPORTOSÍTOTT opciók (címkézett blokkok — `SelectGroup` + `SelectLabel`). Akkor kell, ha a lista
   * RELEVANCIA szerint tagolt: pl. a ticket-formon „a fiók szolgáltatásai" előre, „egyéb szolgáltatás" utána
   * (a legacy `existing`/`other` csoportjai). Üres csoport nem renderel címkét.
   */
  groups?: {
    label: string;
    options: { value: string; label: string; disabled?: boolean; depth?: number }[];
  }[];
}): JSX.Element {
  /**
   * NYITVA VAN-E a lista — EZ különbözteti meg a VALÓDI választást a Radix ÖN-TÖRLÉSÉTŐL.
   *
   * A felhasználó CSAK nyitott listából választhat, és a Radix az `onValueChange`-et még NYITOTT állapotban
   * hívja (a bezárás utána jön). Az ön-törlés ezzel szemben a rejtett natív `<select>`-ből érkezik, teljesen
   * interakció nélkül — tehát zárt listával.
   */
  const nyitva = useRef(false);
  /**
   * Az ÜRES értékű opció felirata — ez lesz a HELYKITÖLTŐ, ha a hívó nem ad sajátot.
   *
   * MIÉRT KELL: a Radix a `''` értéket „nincs érték"-nek tekinti, és ilyenkor a kiválasztott elem szövege
   * helyett a helykitöltőt mutatja. Helykitöltő nélkül tehát a „— nincs —" kiválasztása után a mező ÜRESEN
   * áll — a felhasználó nem látja, hogy a választása megtörtént (mérve a böngészőben). Az opció saját
   * feliratát visszatéve a kiürített mező azt írja ki, ami benne van: „— nincs —".
   */
  const uresOpcioCimke =
    (options ?? []).find((o) => o.value === '')?.label ??
    (groups ?? []).flatMap((g) => g.options).find((o) => o.value === '')?.label;
  return (
    <FormField<TValues>
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label}
            {required && <span className="text-destructive"> *</span>}
          </FormLabel>
          <Select
            value={typeof field.value === 'string' ? field.value : ''}
            // ⚠️ AZ ÜRES ÉRTÉKET CSAK ZÁRT LISTÁRÓL DOBJUK EL — ott ugyanis nem lehet felhasználói választás.
            //
            // MÉRT ÉLES HIBA (1): a Radix a form-integrációhoz egy REJTETT natív `<select>`-et is renderel. Ha
            // a vezérelt érték egy renderre nem ábrázolható benne (üres az opció-lista, vagy a betöltött érték
            // még nincs a listában, mert egy másik lekérésből jön), a böngésző a natív elem értékét eldobja,
            // change-et süt el, a Radix pedig `onValueChange('')`-t hív — és ezzel KITÖRLI a form helyes
            // értékét. A felhasználó ebből annyit lát, hogy „a sidebar nem tölti be az adatot", és mentéskor
            // az ÜRES érték megy el.
            //
            // MÉRT ÉLES HIBA (2) — AZ ELSŐ JAVÍTÁS TÚL SZÉLES VOLT: az `''` feltétel nélküli eldobása kiütötte
            // a VALÓDI „— nincs —" választást is. Négy mező kínál ilyen opciót (fizetőkártya, számlázási
            // gyakoriság, számlázási megállapodás, használat forrása); rajtuk a „nincs"-re kattintás némán nem
            // csinált semmit, vagyis a kitöltött mezőt nem lehetett többé kiüresíteni. (A tévedés gyökere egy
            // ellenőrizetlen feltevés volt: hogy a Radix TILTJA az üres értékű `SelectItem`-et. **Nem tiltja**
            // — megmérve: a lista megnyílik, az opció választható.)
            //
            // MIÉRT A NYITOTT-ÁLLAPOT A HELYES JEL, és miért NEM az opció-lista: próbáltuk úgy is, hogy az
            // üreset akkor engedjük át, ha a mező kínál üres értékű opciót — de az ön-törlés egy RENDERREL
            // KÉSŐBB érkezik, mint amikor az érték ábrázolhatatlan volt, tehát az akkor már bővült listával
            // nem lehet kiszűrni (mérve: a hidratálás beállította a kártyát, a következő render üresre vitte).
            // A lista NYITVA-állapota ezzel szemben magát az interakciót méri, nem a mellékhatását.
            onOpenChange={(o) => {
              nyitva.current = o;
            }}
            onValueChange={(next) => {
              if (next === '' && !nyitva.current) return;
              field.onChange(next);
            }}
            disabled={disabled ?? false}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={placeholder ?? uresOpcioCimke} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {(options ?? []).map((o) => (
                <SelectItem
                  key={o.value}
                  value={o.value}
                  disabled={o.disabled ?? false}
                  depth={o.depth ?? 0}
                >
                  {o.label}
                </SelectItem>
              ))}
              {(groups ?? [])
                .filter((g) => g.options.length > 0)
                .map((g) => (
                  <SelectGroup key={g.label}>
                    <SelectLabel>{g.label}</SelectLabel>
                    {g.options.map((o) => (
                      <SelectItem
                        key={o.value}
                        value={o.value}
                        disabled={o.disabled ?? false}
                        depth={o.depth ?? 0}
                      >
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
            </SelectContent>
          </Select>
          {hint && <p className="text-subtle text-xs">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * KERESHETŐ legördülő választó — ugyanaz a szerződés, mint a `SelectField`-nél (RHF-kötött, `options`
 * value→label párok), de a lista KERESŐVEL nyílik.
 *
 * MIKOR EZT (és nem a `SelectField`-et): ha az opció-lista HOSSZÚ lehet. Mérve az éles adaton: ügyfél 297,
 * fiók 398, kapcsolattartó 475, számlázási profil 286, konstrukció 210, beosztás 34 — egy sima legördülőben
 * ez görgetés-vadászat, és a Radix Select saját típus-ugrása csak az ELSŐ karakterre illeszt, tehát a lista
 * közepén lévő elemet nem lehet „begépelni".
 *
 * Az üres string (`''`) itt is a „nincs kiválasztva" (a `SelectField` konvenciója), így a form-alapértékek
 * és a Zod-sémák változatlanul működnek.
 */
export function SearchableSelectField<TValues extends FieldValues>({
  name,
  label,
  hint,
  disabled,
  required,
  placeholder,
  options,
  onValueChange,
}: BaseFieldProps<TValues> & {
  placeholder?: string;
  options: { value: string; label: string }[];
  /** A FELHASZNÁLÓI választás utáni visszahívás (pl. függő mezők előtöltése) — a programozott `setValue` nem hívja. */
  onValueChange?: (value: string) => void;
}): JSX.Element {
  const comboOptions = options.map((o) => ({ id: o.value, label: o.label }));
  return (
    <FormField<TValues>
      name={name}
      render={({ field }) => {
        const current = typeof field.value === 'string' && field.value !== '' ? field.value : null;
        const selected = comboOptions.find((o) => o.id === current) ?? null;
        return (
          <FormItem>
            <FormLabel>
              {label}
              {required && <span className="text-destructive"> *</span>}
            </FormLabel>
            <FormControl>
              <EntityCombobox
                value={current}
                onChange={(id) => {
                  field.onChange(id ?? '');
                  onValueChange?.(id ?? '');
                }}
                options={comboOptions}
                initialOption={selected}
                placeholder={placeholder}
                disabled={disabled ?? false}
                // A törlés csak akkor, ha a mező nem kötelező (kötelezőnél a „nincs érték" nem érvényes állapot).
                clearable={required !== true}
                aria-label={label}
              />
            </FormControl>
            {hint && <p className="text-subtle text-xs">{hint}</p>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

/**
 * SZEGMENTÁLT (segmented control) form-mező — a `Segmented` primitív form-bekötése (label + hiba + hint).
 *
 * MIKOR EZT a `PillField` HELYETT: ha a választás SEMLEGES dimenzió (típus, címzés-fajta, felület), tehát az
 * opcióknak nincs saját JELENTÉS-SZÍNE. A `PillField` telített, szín-tónusos aktív állapota STÁTUSZ-jellegű
 * választásokhoz való (fizetve/nyitott/…); egy semleges dimenzión az „elsődleges akció" hangsúlyát vitte.
 */
export function SegmentedField<TValues extends FieldValues>({
  name,
  label,
  hint,
  disabled,
  required,
  options,
}: BaseFieldProps<TValues> & { options: readonly SegmentedOption[] }): JSX.Element {
  return (
    <FormField<TValues>
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label}
            {required && <span className="text-destructive"> *</span>}
          </FormLabel>
          <Segmented
            value={typeof field.value === 'string' ? field.value : ''}
            onChange={field.onChange}
            options={options}
            aria-label={label}
            disabled={disabled ?? false}
          />
          {hint && <p className="text-subtle text-xs">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export interface PillOption {
  value: string;
  label: string;
  icon?: ReactNode;
  /** Aktív állapot osztálya (szín-tónus) — pl. bg-success-soft text-success. */
  activeClassName: string;
}

/** Pill-gombos választó (radio-szerű, egy-választós). Szín + ikon az aktív állapoton (pl. státusz). */
export function PillField<TValues extends FieldValues>({
  name,
  label,
  hint,
  disabled,
  required,
  options,
}: BaseFieldProps<TValues> & { options: PillOption[] }): JSX.Element {
  return (
    <FormField<TValues>
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label}
            {required && <span className="text-destructive"> *</span>}
          </FormLabel>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={label}>
            {options.map((o) => {
              const active = field.value === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={disabled}
                  onClick={() => field.onChange(o.value)}
                  className={cn(
                    'ease-atlas inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition-colors duration-150',
                    'disabled:pointer-events-none disabled:opacity-50',
                    active
                      ? cn('border-transparent', o.activeClassName)
                      : 'border-border bg-card text-muted-foreground hover:bg-accent',
                  )}
                >
                  {o.icon}
                  {o.label}
                </button>
              );
            })}
          </div>
          {hint && <p className="text-subtle text-xs">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export interface MultiSegmentedOption {
  value: string;
  label: string;
  /** Akadálymentes teljes név, ha a látható felirat rövidítés (pl. „K" → „kedd"). */
  ariaLabel?: string;
}

/**
 * TÖBB-választós szegmentált sáv (kapcsoló-gombok, `aria-pressed`) — a `SegmentedField` több-választós párja,
 * ugyanazzal a vizuális nyelvvel (halvány sáv, az aktív elem kiemelt háttérrel). Semleges dimenzióhoz (pl. a hét
 * napjai egy ismétlődési szabályban).
 * Az érték a kiválasztott `value`-k tömbje, az opciók SORRENDJÉBEN (nem a kattintás sorrendjében).
 */
export function MultiSegmentedField<TValues extends FieldValues>({
  name,
  label,
  hint,
  disabled,
  required,
  options,
}: BaseFieldProps<TValues> & { options: readonly MultiSegmentedOption[] }): JSX.Element {
  return (
    <FormField<TValues>
      name={name}
      render={({ field }) => {
        const selected = new Set(Array.isArray(field.value) ? (field.value as string[]) : []);
        const toggle = (value: string): void => {
          const next = new Set(selected);
          if (next.has(value)) next.delete(value);
          else next.add(value);
          field.onChange(options.map((o) => o.value).filter((v) => next.has(v)));
        };
        return (
          <FormItem>
            <FormLabel>
              {label}
              {required && <span className="text-destructive"> *</span>}
            </FormLabel>
            <div
              className="bg-muted/40 flex w-fit flex-wrap gap-1 rounded-lg p-1"
              role="group"
              aria-label={label}
            >
              {options.map((o) => {
                const active = selected.has(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    aria-pressed={active}
                    aria-label={o.ariaLabel ?? o.label}
                    disabled={disabled}
                    onClick={() => toggle(o.value)}
                    className={cn(
                      'ease-atlas focus-visible:ring-accent-600 inline-flex min-w-9 items-center justify-center rounded-md px-2.5 py-1.5 text-sm transition-colors duration-150',
                      'focus-visible:outline-none focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50',
                      active
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            {hint && <p className="text-subtle text-xs">{hint}</p>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
